import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import {
  Sparkles, Lock, Calendar, Clock, ArrowRight, CheckCircle2, Loader2,
  GraduationCap, FlaskConical, Stethoscope, History, BarChart3,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRACK_META = {
  JEE: {
    label: 'JEE (Mains + Advanced)',
    description: 'Physics · Chemistry · Mathematics — IIT-level practice',
    Icon: GraduationCap,
    gradient: 'from-indigo-500 via-blue-500 to-cyan-500',
  },
  NEET: {
    label: 'NEET',
    description: 'Physics · Chemistry · Biology — Medical entrance focus',
    Icon: Stethoscope,
    gradient: 'from-rose-500 via-pink-500 to-fuchsia-500',
  },
};

const WEEKLY_VARIANT_META = {
  weekly_mains: { label: 'JEE Mains Pattern', short: 'Mains · 75Q · 3h', Icon: FlaskConical },
  weekly_advanced: { label: 'JEE Advanced Pattern', short: 'Advanced · 54Q · 3h', Icon: Sparkles },
  weekly_neet: { label: 'NEET Pattern', short: 'NEET · 90Q · 3h 20m', Icon: Stethoscope },
};

const DailyMocks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'daily' | 'weekly' | null
  const [history, setHistory] = useState([]);
  const [selectedWeeklyVariant, setSelectedWeeklyVariant] = useState(null);

  const isPro = user?.subscription_status === 'active' || user?.role === 'admin';

  const fetchPreference = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/daily-mocks/preference`);
      setTrack(data.track);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/daily-mocks/history`);
      setHistory(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchPreference();
    fetchHistory();
  }, [fetchPreference, fetchHistory]);

  const setExamTrack = async (newTrack) => {
    try {
      await axios.post(`${API_URL}/api/daily-mocks/preference`, { track: newTrack });
      setTrack(newTrack);
      toast.success(`Locked in ${newTrack}. Time to grind.`);
    } catch (e) {
      toast.error('Failed to save preference');
    }
  };

  const startDaily = async () => {
    if (!isPro) {
      navigate('/upgrade');
      return;
    }
    setBusy('daily');
    toast.info('Generating your daily mock — this can take up to 30s the first time today.', { duration: 5000 });
    try {
      const { data } = await axios.get(`${API_URL}/api/daily-mocks/today`);
      navigate(`/mock/${data.id}`, { state: { mock: data, type: 'daily' } });
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      const msg = e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to load daily mock';
      if (code === 'PRO_REQUIRED') {
        toast.error('Pro required for daily mocks');
        navigate('/upgrade');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Try again in a moment');
      }
    } finally {
      setBusy(null);
    }
  };

  const startWeekly = async (variant) => {
    setBusy('weekly');
    toast.info('Loading your weekly full-length mock — please wait up to 45s for AI generation.', { duration: 5000 });
    try {
      const url = variant ? `${API_URL}/api/daily-mocks/weekly?variant=${variant}` : `${API_URL}/api/daily-mocks/weekly`;
      const { data } = await axios.get(url);
      navigate(`/mock/${data.id}`, { state: { mock: data, type: 'weekly' } });
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      const msg = e.response?.data?.detail?.message || 'Failed to load weekly mock';
      if (code === 'WEEKLY_LIMIT_REACHED') {
        toast.error('Free tier: 1 weekly mock used. Upgrade to Pro for unlimited.');
        navigate('/upgrade');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Try again');
      }
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-[#0EA5E9]" />
        </div>
      </div>
    );
  }

  // ===== Track picker =====
  if (!track) {
    return (
      <div className="min-h-screen" data-testid="daily-mocks-page">
        <Header />
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-5xl font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Pick Your Exam Track
            </h1>
            <p className="text-base text-[#6B7280] max-w-md mx-auto">
              We'll customize your daily AI-generated mocks to this exam pattern. You can change this later from Profile.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(TRACK_META).map(([key, meta]) => {
              const Icon = meta.Icon;
              return (
                <button
                  key={key}
                  onClick={() => setExamTrack(key)}
                  data-testid={`track-option-${key.toLowerCase()}`}
                  className="quiz-card text-left p-7 hover:shadow-xl transition-all hover:-translate-y-1 group"
                >
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-lg mb-4`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {meta.label}
                  </h3>
                  <p className="text-sm text-[#6B7280] mb-4">{meta.description}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-[#0EA5E9] group-hover:gap-2 transition-all">
                    Select <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  const trackMeta = TRACK_META[track];
  const weeklyVariants = track === 'JEE' ? ['weekly_mains', 'weekly_advanced'] : ['weekly_neet'];

  return (
    <div className="min-h-screen" data-testid="daily-mocks-page">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Hero */}
        <div className="flex items-start gap-4 mb-10">
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${trackMeta.gradient} flex items-center justify-center shadow-lg shrink-0`}>
            <trackMeta.Icon className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {trackMeta.label} Mocks
            </h1>
            <p className="text-sm text-[#6B7280] mt-1">
              AI-generated. Fresh every day. <button onClick={() => setTrack(null)} data-testid="change-track-btn" className="text-[#0EA5E9] hover:underline ml-1 font-medium">Change track</button>
            </p>
          </div>
        </div>

        {/* Daily Quick Mock */}
        <section className="mb-8" data-testid="daily-mock-section">
          <div className={`relative overflow-hidden rounded-3xl p-7 ${isPro ? 'bg-gradient-to-br from-indigo-50 via-white to-cyan-50 border border-indigo-100' : 'bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200'}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs font-black uppercase tracking-widest text-indigo-700">Today's Quick Mock</span>
                  {!isPro && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">PRO ONLY</span>}
                </div>
                <h2 className="text-2xl font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  30 Questions · 45 minutes
                </h2>
                <p className="text-sm text-[#6B7280] mb-1">
                  Daily personalised practice across {track === 'JEE' ? 'Physics, Chemistry, Math' : 'Physics, Chemistry, Biology'}. Auto-graded with subject-wise analytics.
                </p>
                <div className="flex items-center gap-4 mt-3 text-xs text-[#6B7280]">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> 45 min</span>
                  <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI-curated</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> +4 / -1 marking</span>
                </div>
              </div>
              <button
                onClick={startDaily}
                disabled={busy === 'daily'}
                data-testid="start-daily-mock-btn"
                className={`px-6 py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all whitespace-nowrap ${
                  isPro
                    ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-[0_4px_0_rgb(67,56,202)] active:translate-y-1 active:shadow-none hover:from-indigo-700 hover:to-blue-700'
                    : 'bg-amber-500 text-white shadow-[0_4px_0_rgb(180,83,9)] active:translate-y-1 active:shadow-none hover:bg-amber-600'
                }`}
              >
                {busy === 'daily' ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                ) : isPro ? (
                  <>Start Today's Mock <ArrowRight className="w-4 h-4" /></>
                ) : (
                  <><Lock className="w-4 h-4" /> Unlock with Pro</>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Weekly Full-Length Mock */}
        <section className="mb-10" data-testid="weekly-mock-section">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-[#0EA5E9]" />
            <h2 className="text-xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              This Week's Full-Length Mock
            </h2>
            {!isPro && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">FREE · 1/week</span>}
          </div>
          <div className={`grid grid-cols-1 ${weeklyVariants.length > 1 ? 'md:grid-cols-2' : ''} gap-5`}>
            {weeklyVariants.map((variant) => {
              const meta = WEEKLY_VARIANT_META[variant];
              const Icon = meta.Icon;
              return (
                <div key={variant} className="quiz-card p-6" data-testid={`weekly-variant-${variant}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{meta.label}</h3>
                      <p className="text-xs text-[#6B7280] mt-0.5">{meta.short}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedWeeklyVariant(variant); startWeekly(variant); }}
                    disabled={busy === 'weekly'}
                    data-testid={`start-weekly-${variant}-btn`}
                    className="w-full mt-5 px-5 py-3 rounded-2xl font-bold text-sm transition-all bg-[#0EA5E9] text-white shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none hover:bg-[#0284C7] flex items-center justify-center gap-2"
                  >
                    {busy === 'weekly' && selectedWeeklyVariant === variant ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</>
                    ) : (
                      <>Start Full Mock <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* History */}
        {history.length > 0 && (
          <section data-testid="mock-history-section">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-[#6B7280]" />
              <h2 className="text-xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Recent Attempts</h2>
            </div>
            <div className="quiz-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold">Type</th>
                    <th className="px-4 py-3 text-left font-bold">Score</th>
                    <th className="px-4 py-3 text-left font-bold">Accuracy</th>
                    <th className="px-4 py-3 text-left font-bold">When</th>
                  </tr>
                </thead>
                <tbody>
                  {history.slice(0, 10).map((h, idx) => (
                    <tr key={`${h.mock_id}-${idx}`} className="border-t border-slate-100" data-testid={`history-row-${idx}`}>
                      <td className="px-4 py-3 font-semibold capitalize text-[#111827]">
                        {(h.mock_type_at_attempt || '').replace('weekly_', 'Weekly ').replace('daily', 'Daily Quick')}
                      </td>
                      <td className="px-4 py-3 font-black text-[#0EA5E9]">{h.score} / {h.total}</td>
                      <td className="px-4 py-3 text-[#6B7280]">
                        {h.correct + h.wrong + h.unattempted > 0
                          ? `${Math.round((h.correct / (h.correct + h.wrong + h.unattempted)) * 100)}%`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-[#9CA3AF]">{h.submitted_at ? new Date(h.submitted_at).toLocaleString() : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Upgrade nudge for free users */}
        {!isPro && (
          <div className="mt-10 p-6 rounded-3xl bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-[0_6px_0_rgb(180,83,9)]" data-testid="upgrade-nudge">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-black mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>Unlock Daily Mocks with Pro</h3>
                <p className="text-sm opacity-95">₹299/month · Daily AI mocks · Unlimited weekly variants · AI Doubt Solver · Weakness reports</p>
              </div>
              <Link
                to="/upgrade"
                className="px-5 py-3 rounded-2xl bg-white text-amber-600 font-black text-sm shadow-md hover:shadow-lg transition-all whitespace-nowrap"
                data-testid="go-upgrade-btn"
              >
                Go Pro
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DailyMocks;
